import {
  ButtonSecondary,
  cssColorValue,
  screenMdMin,
  semanticColors,
  spacer12,
  spacer16,
} from '@spotify-internal/encore-web';
import React from 'react';
import styled from 'styled-components';

import { MastheadLink, MastheadLinkOrLinkListArray } from 'lib/sanity/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { urlForImage } from 'utils/sanity';
import { NavBarLanguagePicker } from 'components/Masthead/Header/LanguagePicker';
import { ProfileLinksPopoverTrigger } from 'components/Masthead/Header/Profile/PopoverTrigger';

const LinksWrapper = styled.section`
  & > ul {
    align-items: center;
    display: flex;
    gap: ${spacer12};
    list-style: none;
    margin: 0;
    padding: 0;
  }

  @media (max-width: ${screenMdMin}) {
    display: none;
  }
`;

const PrimaryButton = styled(ButtonSecondary)`
  background-color: ${cssColorValue(semanticColors.textBase)};
  border: 1px solid ${cssColorValue(semanticColors.textBase)};
  color: ${cssColorValue(semanticColors.backgroundBase)};

  &:hover {
    border: 1px solid ${cssColorValue(semanticColors.textBase)};
  }
`;

const SecondaryButton = styled(ButtonSecondary)`
  background: ${cssColorValue(semanticColors.backgroundBase)};
  border: 1px solid ${cssColorValue(semanticColors.backgroundBase)};
  color: ${cssColorValue(semanticColors.textBase)};

  &:hover {
    background: ${cssColorValue(semanticColors.backgroundBase)};
  }
`;

const OutlineButton = styled(ButtonSecondary)`
  background: transparent;
  border: 1px solid ${cssColorValue(semanticColors.textBase)};
  color: ${cssColorValue(semanticColors.textBase)};

  &:hover {
    background: transparent;
  }
`;

const IconLeadingImage = styled.img<{
  imgUrl: string;
}>`
  background-color: ${cssColorValue(semanticColors.textBase)};
  mask: ${({ imgUrl }) => `url(${imgUrl}) no-repeat center`};
  height: ${spacer16};
  width: ${spacer16};
`;

type Props = {
  secondaryLinks?: MastheadLink[];
  profileLinks?: MastheadLinkOrLinkListArray;
};

export const NavBarSecondaryLinks = ({ secondaryLinks, profileLinks }: Props) => {
  const {
    verticalConfig: { features, verticalName },
  } = useServerSideConstants();
  const { showHeaderGradient } = features;
  const SecondaryBtn = showHeaderGradient ? SecondaryButton : OutlineButton;

  const renderSecondaryLinkItem = (item: MastheadLink) => {
    const iconLeadingSrc =
      item.image && urlForImage({ source: item.image.asset, maxWidth: 16, vertical: verticalName });

    if (item.variant === 'primaryButton') {
      return (
        <PrimaryButton
          iconLeading={iconLeadingSrc ? () => <IconLeadingImage imgUrl={iconLeadingSrc} alt="" /> : undefined}
          size="small"
          key={item.title}
          href={item.href}
          target={item.target}
        >
          {item.title}
        </PrimaryButton>
      );
    }

    if (item.variant === 'secondaryButton') {
      return (
        <SecondaryBtn
          iconLeading={iconLeadingSrc ? () => <IconLeadingImage imgUrl={iconLeadingSrc} alt="" /> : undefined}
          size="small"
          key={item.title}
          href={item.href}
          target={item.target}
        >
          {item.title}
        </SecondaryBtn>
      );
    }

    return null;
  };

  return (
    <LinksWrapper>
      <ul>
        <li>
          <NavBarLanguagePicker container="header" />
        </li>
        {secondaryLinks?.map(renderSecondaryLinkItem)}
        {profileLinks && <ProfileLinksPopoverTrigger profileLinks={profileLinks} />}
      </ul>
    </LinksWrapper>
  );
};
